import { z } from "zod";

export const NotificationChannelEnum = z.enum(["inapp", "email", "webhook"]);

export const NotificationStateEnum = z.enum(["pending", "sent", "failed"]);

export const NotificationSchema = z.object({
  id: z.string().uuid(),
  savedSearchRunId: z.string().uuid(),
  recipientId: z.string().uuid(),
  channel: NotificationChannelEnum,
  state: NotificationStateEnum,
  payload: z.record(z.string(), z.unknown()).optional().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Notification = z.infer<typeof NotificationSchema>;

// ── In-app inbox (C16) ──────────────────────────────────────────────────────

/**
 * A single in-app inbox item as returned to the frontend. Distinct from the
 * raw {@link NotificationSchema} row: it surfaces only ``inapp`` notifications
 * and adds the read/dismiss lifecycle the inbox UI needs.
 */
export const InboxItemSchema = z.object({
  id: z.string().uuid(),
  savedSearchRunId: z.string().uuid(),
  documentId: z.string().uuid().nullable(),
  /** Free-form rendering payload (title/snippet/url). */
  payload: z.record(z.string(), z.unknown()).nullable(),
  /** ``null`` until the recipient marks it read. */
  readAt: z.string().datetime().nullable(),
  /** ``null`` until the recipient dismisses it. */
  dismissedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export type InboxItem = z.infer<typeof InboxItemSchema>;

/** ``GET /notifications/inbox`` response. */
export const ListInboxResponseSchema = z.object({
  items: z.array(InboxItemSchema),
  unreadCount: z.number().int().nonnegative(),
});

export type ListInboxResponse = z.infer<typeof ListInboxResponseSchema>;

/** ``GET /notifications/inbox/unread-count`` response. */
export const UnreadCountResponseSchema = z.object({
  unreadCount: z.number().int().nonnegative(),
});

export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

// ── Preferences (C16) ───────────────────────────────────────────────────────

export const DigestCadenceEnum = z.enum(["off", "daily", "weekly"]);

export type DigestCadence = z.infer<typeof DigestCadenceEnum>;

/**
 * Per-channel notification preferences. A user has one *global* preference set;
 * each saved search may override it. Disabling one channel never disables the
 * others — every channel toggle is independent.
 */
export const NotificationPreferencesSchema = z
  .object({
    /** Persist in-app inbox items. */
    inappEnabled: z.boolean(),
    /** Send transactional / digest email. */
    emailEnabled: z.boolean(),
    /** POST to the user's configured webhook endpoints. */
    webhookEnabled: z.boolean(),
    /** ``off`` → per-search emails; ``daily``/``weekly`` → rolled-up digest. */
    digestCadence: DigestCadenceEnum,
  })
  .strict();

export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

/** Documented global defaults: in-app + email on, webhook off, no digest. */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  inappEnabled: true,
  emailEnabled: true,
  webhookEnabled: false,
  digestCadence: "off",
};

/**
 * ``PATCH`` body for global or per-search preferences — every field optional so
 * a single channel can be toggled in isolation. A per-search patch sets an
 * override; clearing a field (``null``) is not supported here (delete the
 * override row instead).
 */
export const UpdateNotificationPreferencesRequestSchema = z
  .object({
    inappEnabled: z.boolean().optional(),
    emailEnabled: z.boolean().optional(),
    webhookEnabled: z.boolean().optional(),
    digestCadence: DigestCadenceEnum.optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: "At least one preference field must be supplied.",
  });

export type UpdateNotificationPreferencesRequest = z.infer<
  typeof UpdateNotificationPreferencesRequestSchema
>;

/** ``GET /notifications/preferences`` response — global + per-search overrides. */
export const NotificationPreferencesResponseSchema = z.object({
  global: NotificationPreferencesSchema,
  perSearch: z.array(
    z.object({
      savedSearchId: z.string().uuid(),
      preferences: NotificationPreferencesSchema,
    }),
  ),
});

export type NotificationPreferencesResponse = z.infer<typeof NotificationPreferencesResponseSchema>;

// ── Webhook endpoints (C16) ─────────────────────────────────────────────────

/** Lifecycle of a per-user webhook endpoint (circuit breaker per URL). */
export const WebhookEndpointStatusEnum = z.enum(["active", "disabled"]);

export type WebhookEndpointStatus = z.infer<typeof WebhookEndpointStatusEnum>;

/**
 * A webhook endpoint as returned by any *read* API. The signing secret is
 * NEVER included here — it is shown exactly once on creation (see
 * {@link CreateWebhookEndpointResponseSchema}) and only its hash is persisted.
 */
export const WebhookEndpointSchema = z.object({
  id: z.string().uuid(),
  url: z.string().url(),
  status: WebhookEndpointStatusEnum,
  /** Consecutive delivery failures; reset to 0 on success. */
  consecutiveFailures: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type WebhookEndpoint = z.infer<typeof WebhookEndpointSchema>;

/** ``POST /notifications/webhooks`` request body. */
export const CreateWebhookEndpointRequestSchema = z
  .object({
    url: z.string().url().max(2048),
  })
  .strict();

export type CreateWebhookEndpointRequest = z.infer<typeof CreateWebhookEndpointRequestSchema>;

/**
 * ``POST /notifications/webhooks`` response. The raw ``secret`` appears here
 * once and nowhere else; the client must store it to verify the
 * ``X-RecordsMonitor-Signature`` HMAC on delivered payloads.
 */
export const CreateWebhookEndpointResponseSchema = z.object({
  endpoint: WebhookEndpointSchema,
  /** Shown exactly once; never returned by any read API. */
  secret: z.string().min(1),
});

export type CreateWebhookEndpointResponse = z.infer<typeof CreateWebhookEndpointResponseSchema>;

/** ``GET /notifications/webhooks`` response. */
export const ListWebhookEndpointsResponseSchema = z.object({
  endpoints: z.array(WebhookEndpointSchema),
});

export type ListWebhookEndpointsResponse = z.infer<typeof ListWebhookEndpointsResponseSchema>;

/** ``POST /notifications/webhooks/:id/test`` response. */
export const TestWebhookResponseSchema = z.object({
  delivered: z.boolean(),
  statusCode: z.number().int().nullable(),
  attempts: z.number().int().positive(),
});

export type TestWebhookResponse = z.infer<typeof TestWebhookResponseSchema>;

// ── SendGrid Event Webhook (bounce / spam / drop) ───────────────────────────

/**
 * A single event from SendGrid's Event Webhook. SendGrid POSTs an *array* of
 * these; we validate only the fields we consume to suppress a recipient and
 * ignore the rest (``passthrough`` so unknown future fields do not 400).
 *
 * The ``event`` field is the delivery outcome (``bounce``, ``dropped``,
 * ``spamreport``, ``delivered`` …); for a bounce, ``type`` distinguishes a
 * hard ``bounce`` from a transient ``blocked``.
 */
export const SendGridEventSchema = z
  .object({
    email: z.string().email().optional(),
    event: z.string(),
    type: z.string().optional(),
    reason: z.string().optional(),
  })
  .passthrough();

export type SendGridEvent = z.infer<typeof SendGridEventSchema>;

/** The Event Webhook request body: a batch of events. */
export const SendGridInboundEventsSchema = z.array(SendGridEventSchema);

export type SendGridInboundEvents = z.infer<typeof SendGridInboundEventsSchema>;

/** The signature header carried on every webhook delivery. */
export const WEBHOOK_SIGNATURE_HEADER = "X-RecordsMonitor-Signature";
